import * as SYSTEM from '@/constant/system/code'
import Router from '@/views/system/common/router/index.vue';
import Layout from '@/layout/Layout.vue'
import { RouteMeta, RouteRecordRaw } from 'vue-router';
import { BOOL } from '@/constant/system/enums';
import _ from 'lodash'
import { formatRouter } from './common';

/**
 * 路由工具
 *
 * @author Jay Qing
 */

/**
 * 扁平化路由
 * 将多级路由转换为二级路由
 *
 * @param routes
 * @return {*[]|*}
 */
export function flatRoutes(routes: RouteRecordRaw[]) {
  if (!routes || routes.length === 0) {
    return []
  }
  const clonedRoutes = _.cloneDeep(routes)
  for (const route of clonedRoutes) {
    route.children = flatChildrenRoutes(route.path, _.cloneDeep(route.children || []))
  }
  return clonedRoutes
}

export function flatChildrenRoutes(root: string, routes: RouteRecordRaw[]) {
  const result: RouteRecordRaw[] = []
  if (!routes || routes.length === 0) {
    return result
  }
  for (const route of routes) {
    if (route.meta?.style === SYSTEM.MENU_STYLE.MENU) {
      route.path = concatRoutePath(root, route.path)
      result.push(route)
    } else if (route.meta?.style === SYSTEM.MENU_STYLE.CATALOG) {
      result.push(...flatChildrenRoutes(concatRoutePath(root, route.path), route.children || []))
    }
  }
  return result
}

export function concatRoutePath(root: string, path: string) {
  const delimiter = '/'
  let full = root
  if (!_.startsWith(path, delimiter) &&
    !_.endsWith(root, delimiter)) {
    full += delimiter
  } else if (_.startsWith(path, delimiter) &&
    _.endsWith(root, delimiter)) {
    full = full.substring(0, full.length - 1)
  }
  return full + path
}

export function generateCatalogRoute(menus: AppMenu[]) {
  const routes: RouteRecordRaw[] = [];
  if (!menus || menus.length === 0) {
    return routes;
  }
  menus.forEach(menu => {
    const route = convertMenu2Route(menu);
    if (menu.menuType === SYSTEM.MENU_TYPE.URL.CODE) {
      convertIfUrlRoute(menu, route);
    } else {
      route.path = '/' + route.path;
      route.component = Layout;
      route.redirect = 'noRedirect';
    }
    if(!route.meta) {
      route.meta = {}
    }
    route.meta.style = SYSTEM.MENU_STYLE.CATALOG;
    route.meta.alwaysShow = true;
    if (menu.children != null && menu.children.length > 0) {
      route.children = generateChildrenRoute(menu.children);
    }
    routes.push(route);
  });
  return routes;
}

export function generateChildrenRoute(menus: AppMenu[]) {
  const routes: RouteRecordRaw[] = [];
  if (!menus || menus.length < 1) {
    return routes;
  }
  menus.forEach(menu => {
    const route = convertMenu2Route(menu);
    if(!route.meta) {
      route.meta = {}
    }
    if (menu.menuType === SYSTEM.MENU_TYPE.URL.CODE) {
      route.meta.style = SYSTEM.MENU_STYLE.MENU;
      convertIfUrlRoute(menu, route);
    } else {
      if (menu.menuStyle === SYSTEM.MENU_STYLE.CATALOG) {
        route.component = Router;
        route.redirect = 'noRedirect';
        route.meta.alwaysShow = true;
        route.meta.style = SYSTEM.MENU_STYLE.CATALOG;
      } else if (menu.menuStyle === SYSTEM.MENU_STYLE.MENU) {
        route.component = lazyLoadView(menu.component);
        route.meta.noCache = menu.keepAlive !== BOOL.TRUE;
        route.meta.style = SYSTEM.MENU_STYLE.MENU;
      }
    }
    const children = menu.children;
    if (children != null && children.length > 0) {
      route.children = generateChildrenRoute(children);
    }
    routes.push(route);
  });
  return routes;
}

export function convertIfUrlRoute(menu: AppMenu, route: RouteRecordRaw) {
  if (menu.menuType !== SYSTEM.MENU_TYPE.URL.CODE) {
    return;
  }
  const com = menu.component;
  if (com.startsWith(SYSTEM.URL_TYPE.FORWARD)) {
    route.path = com.substring(com.indexOf(':') + 1, com.length);
    delete route.component;
  } else if (com.startsWith(SYSTEM.URL_TYPE.IFRAME)) {
    if(!route.meta) {
      route.meta = {}
    }
    route.meta.url = com.substring(com.indexOf(':') + 1, com.length);
    route.component = lazyLoadView('system/common/iframe/index.vue', false)
  }
}

export function convertMenu2Route(menu: AppMenu) {
  const route = {} as RouteRecordRaw;
  const meta = {} as RouteMeta;
  route.path = menu.path;
  // 路由地址转换为组件名以适配缓存
  route.name = _.replace(_.startCase(menu.path), /\s+/g, '');
  meta.title = menu.menuName;
  meta.icon = menu.icon;
  meta.hidden = menu.visible === SYSTEM.MENU_VISIBILITY.INVISIBLE.CODE;
  route.meta = meta;
  return route;
}

// 路由懒加载
export const lazyLoadView = (view: string, split = true) => {
  if(split) {
    const pathArr = view.split('/')
    const cname = view + '/' +formatRouter(pathArr[pathArr.length - 1]) + '.vue'
    return () => Promise.resolve(require(`@/views/${cname}`))
  } 
  
  return () => Promise.resolve(require(`@/views/${view}`))
}